import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const OPEN_BONUS_TAP_OPTIONS = [5, 10, 15, 20];

const OpenBonusModal = ({ onClose, user, setUser }) => {
  const [loading, setLoading] = useState(null); // 'ad' | 'stars' | null
  const [error, setError] = useState('');
  const openBonusAdRef = useRef(null);

  // Pick a random tap amount to display and send to backend so granted amount matches
  const [displayTapBonus] = useState(
    () => OPEN_BONUS_TAP_OPTIONS[Math.floor(Math.random() * OPEN_BONUS_TAP_OPTIONS.length)],
  );

  useEffect(() => {
    const blockId = '22783';
    if (typeof window !== 'undefined' && window.Adsgram && blockId && !openBonusAdRef.current) {
      try {
        openBonusAdRef.current = window.Adsgram.init({ blockId });
      } catch (e) {
        console.warn('[Adsgram] Open bonus init failed:', e);
      }
    }
  }, []);

  const handleWatchAd = async () => {
    setError('');
    if (!openBonusAdRef.current) {
      setError('Ad service not available');
      return;
    }
    setLoading('ad');
    try {
      await openBonusAdRef.current.show();
      // Call backend to grant open bonus with same amount we showed (can exceed max)
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/open-bonus-video-complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ taps: displayTapBonus }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && typeof data.credits === 'number') {
        setUser((prev) => ({ ...prev, credits: data.credits }));
        onClose();
      } else if (res.status === 429) {
        setError(data?.message || 'Please wait before claiming again.');
      } else {
        setError(data?.error || 'Could not grant bonus');
      }
    } catch (err) {
      if (err?.error) {
        setError('Ad failed to load – try again');
      } else {
        setError('Ad was skipped or closed');
      }
    } finally {
      setLoading(null);
    }
  };

  const handleDoubleTapsStars = async () => {
    setError('');
    const isTMA = typeof window !== 'undefined' && !!window.Telegram?.WebApp?.openInvoice;
    if (!isTMA) {
      setError('Double taps with Stars is only available in the Telegram app.');
      return;
    }
    setLoading('stars');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/double-taps-stars/create-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(
          res.status === 429
            ? data?.message || 'Please wait 2 hours between bonus uses.'
            : data?.error || 'Could not start payment',
        );
        setLoading(null);
        return;
      }
      const { orderId, invoiceLink } = data;
      window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
        if (status !== 'paid') {
          setError(status === 'cancelled' ? 'Payment cancelled' : 'Payment failed');
          setLoading(null);
          return;
        }
        let tries = 0;
        const maxTries = 20;
        const wait = (ms) => new Promise((r) => setTimeout(r, ms));
        while (tries < maxTries) {
          const chk = await fetch(
            `${process.env.REACT_APP_API_URL}/api/telegram/stars/order/${orderId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            },
          );
          if (chk.ok) {
            const j = await chk.json();
            if (j.status === 'paid') break;
          }
          tries++;
          await wait(1500);
        }
        const statusRes = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const statusData = await statusRes.json().catch(() => ({}));
        if (statusRes.ok && typeof statusData.credits === 'number') {
          setUser((prev) => ({ ...prev, credits: statusData.credits }));
        }
        setLoading(null);
        onClose();
      });
    } catch (e) {
      setError(e?.message || 'Failed to start payment');
      setLoading(null);
    }
  };

  const busy = loading !== null;

  return (
    <div className="modal_box">
    <div className="modal_title">BONUS<button className="modal_close" onClick={onClose}/></div>
      <motion.div className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
        <div className="shadow2" style={{fontWeight: 600, fontSize: 28}}>
          🎁<br/>Bonus Taps
        </div>
        <p
        className="shadow1"
          style={{
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          GET EXTRA TAPS!
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <button
            type="button"
            className="game-button green-button"
            onClick={handleWatchAd}
            disabled={busy}
            style={{
              padding: '14px 20px',
              borderRadius: 12,
              background: 'linear-gradient(180deg, #5a9c4a, #3d7a32)',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              fontSize: 16,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy && loading !== 'ad' ? 0.6 : 1,
            }}
          >
            {loading === 'ad' ? 'Watching ad…' : `📺 Free bonus: +${displayTapBonus} taps`}
          </button>

          <button
            type="button"
            className="game-button yellow-button"
            onClick={handleDoubleTapsStars}
            disabled={busy}
            style={{
              padding: '14px 20px',
              borderRadius: 12,
              background: 'linear-gradient(180deg, #7b5cbf, #5a3d9e)',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              fontSize: 16,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy && loading !== 'stars' ? 0.6 : 1,
            }}
          >
            {loading === 'stars' ? 'Opening payment…' : '⭐ Double your taps — 40 Stars'}
          </button>
        </div>

        {error && (
          <p
            style={{
              color: '#ff8a80',
              fontSize: 14,
              marginBottom: 12,
            }}
          >
            {error}
          </p>
        )}

      </div>
        <div className="modal_footer">
          <button
            className="game-button red-button"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            MAYBE LATER
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default OpenBonusModal;
